/**
 * North Houston District (Proposal 02) — 5-Year Financial Workbook generator.
 *
 * Mirrors the Baytown spreadsheet structure but is 100% scoped to NH data
 * (1,200 households · 3 Spokes × 400 · ~$3.46M Year-1). No shared imports
 * with Baytown's proposalData.js — only northHoustonData.js.
 */
import * as XLSX from "xlsx";
import {
  nhCentralHubLines,
  nhAgencyBudgetLines,
  nhPortfolioContracts,
  nhSystemTotals,
  nhFiveYearForecast,
  nhFiveYearGrandTotal,
  nhFiveYearAllInTotal,
  nhTriPortfolioROI,
  nhOperationalPhases,
  nhReferralPartners,
  nhProgramGoalsForecast,
  nhProgramGoalsTotals,
  nhProgramOutcomeBenchmarks,
} from "./northHoustonData";

// NH-specific Sterile Mate Developer Group (matches BudgetGateNH.jsx)
const nhDevGroup = {
  team: [
    { role: "Principal Consultant & Program Creator", who: "George Broughton III · Sterile Mate LLC", detail: "Founding Architect of the model. Leads development, train-the-trainer, and 2-year handoff.", cost: 135000 },
    { role: "LCSW · Clinical Curriculum Lead", who: "Sub-contracted Houston-area LCSW", detail: "Trauma-informed care curriculum, clinical supervision protocols, intake matrix design.", cost: 95000 },
    { role: "Senior Living Social Worker", who: "Sub-contracted Texas-licensed senior-care SW", detail: "Senior Care Pipeline curriculum: aging-in-place, AAA navigation, in-home care coordination.", cost: 85000 },
    { role: "Employment Specialist", who: "Sub-contracted workforce-development expert", detail: "Trade Pipeline curriculum: Lone Star North Harris, HCC North, Workforce Solutions, Amazon Career Choice.", cost: 75000 },
    { role: "Continuing Education Specialist", who: "Sub-contracted adult-ed / training designer", detail: "Builds the connector recertification pipeline, manages the train-the-trainer materials library.", cost: 75000 },
  ],
  contract: [
    { period: "Year 1 (Firm)", phase: "Develop · Build · Train", cost: 515000, detail: "Months 1–6: program development. Months 7–12: train-the-trainer rollout to Hub + 3 Spokes." },
    { period: "Year 2 (Firm)", phase: "Stabilize · Audit · Hand off", cost: 257500, detail: "50% engagement. Quarterly fidelity audits, connector recertification, agency-supervisor coaching." },
    { period: "Year 3 (Option)", phase: "Supervised Independence", cost: 125000, detail: "25% engagement at the District's option. Spot reviews, annual recertification refresh." },
    { period: "Year 4 (Option)", phase: "Advisory Only", cost: 75000, detail: "Quarterly advisory retainer at the District's option. NH executive team owns operations." },
    { period: "Year 5 (Option)", phase: "Advisory Only", cost: 50000, detail: "Annual advisory check-in at the District's option. Full NH independence." },
  ],
  totals: { year1: 515000, year2: 257500, twoYearFirm: 772500, fiveYearAllOptions: 1022500, teamSalariesYr1: 465000, directExpensesYr1: 50000 },
};

// Funding Sources Stack (matches BudgetGateNH.jsx)
const nhFundingSources = [
  { source: "HUD CDBG · Houston + Harris County entitlement", pct: 40, amount: 1382224, note: "Public Services for Vulnerable Populations (eligible CDBG activity) — primary anchor. Section 108 loan capacity available for multi-year commitment." },
  { source: "Anchor Employer + Philanthropic Match", pct: 20, amount: 691112, note: "Houston Endowment, Kinder Foundation, JPMorgan PolicyCenter, Amazon Career Choice, Sysco corporate, FedEx Cares — funders prefer matching public dollars 1:1." },
  { source: "Workforce Solutions Greater Houston (WIOA)", pct: 15, amount: 518334, note: "Already-appropriated WIOA Title I + Childcare Subsidy dollars — pays for the trade-pipeline, CDL training, and childcare-during-training portions directly." },
  { source: "Harris County ARPA / General Fund", pct: 12, amount: 414667, note: "Commissioners Court has appropriated similar service-coordination dollars previously. Precinct 1 is the natural champion." },
  { source: "HUD ESG / Continuum of Care", pct: 8, amount: 276445, note: "Eviction-prevention + housing-stability components are explicitly ESG/CoC eligible. Coalition for the Homeless is the pass-through." },
  { source: "TIRZ #11 · Greater Greenspoint Redevelopment Authority", pct: 5, amount: 172778, note: "District-bounded community-development dollars — local match that strengthens every federal application." },
];

// Regional Hub Consolidation scenarios (matches BudgetGateNH.jsx)
const nhRegionalScenarios = [
  { label: "1 Deployment", deployments: 1, households: 1200, hubTotal: 706060, hubPerDeployment: 706060, hubPerHH: 588, note: "Today's NH Year-1 launch (this proposal)" },
  { label: "2 Deployments", deployments: 2, households: 2700, hubTotal: 810000, hubPerDeployment: 405000, hubPerHH: 300, note: "Add Baytown (1,500 HH). Hub grows ~15%, supervises both." },
  { label: "3 Deployments", deployments: 3, households: 4200, hubTotal: 920000, hubPerDeployment: 306667, hubPerHH: 219, note: "Add Aldine/Pasadena (1,500 HH). Hub at ~$307k per deployment." },
  { label: "4 Deployments", deployments: 4, households: 5700, hubTotal: 1040000, hubPerDeployment: 260000, hubPerHH: 182, note: "County-wide. Hub at ~$260k per deployment. Per-HH cost = $182." },
];

// ---------- helpers ----------
const num = (n) => (typeof n === "number" ? n : 0);
function buildSheet(rows, opts = {}) {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  if (opts.colWidths) ws["!cols"] = opts.colWidths.map((w) => ({ wch: w }));
  if (opts.merges) ws["!merges"] = opts.merges;
  return ws;
}

// ---------- 1. Executive Summary ----------
function execSummarySheet() {
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const rows = [
    ["NORTH HOUSTON DISTRICT · PROPOSAL 02 · THE BRIDGE MODEL"],
    ["5-Year Financial Workbook — Service Connector Initiative · IAH Corridor"],
    [`Generated: ${today}  |  Status: PROPOSED — PENDING DISTRICT BOARD APPROVAL — NOT YET ADOPTED`],
    [],
    ["EXECUTIVE SUMMARY"],
    [],
    ["Metric", "Year 1", "5-Year Total"],
    ["Households Served (Annual)", nhSystemTotals.householdsServed, nhSystemTotals.householdsServed * 5],
    ["Cost per Household (Recurring)", nhSystemTotals.costPerHousehold, "—"],
    ["Cost per Household (All-In, incl. Dev Group)", nhSystemTotals.costPerHouseholdAllIn, "—"],
    ["Service-Connector Caseload Cap", `1 : ${nhSystemTotals.caseload}`, "—"],
    ["Spokes (Competitive RFP)", `${nhSystemTotals.agencyCount} agencies × 400 HH`, "—"],
    [],
    ["BUDGET HEADLINES (Year 1)"],
    ["Central Coordination Hub (North Houston District)", num(nhSystemTotals.centralHub)],
    ["Per Agency / Spoke (400 HH)", num(nhSystemTotals.perAgency)],
    [`× ${nhSystemTotals.agencyCount} Agencies`, num(nhSystemTotals.totalAgencies)],
    ["Recurring Subtotal (Hub + 3 Agencies)", num(nhSystemTotals.grandTotal)],
    ["Sterile Mate Developer Group · Year 1", num(nhDevGroup.totals.year1)],
    ["TOTAL YEAR 1 INVESTMENT (All-In)", num(nhSystemTotals.grandTotalAllIn)],
    [],
    ["5-YEAR TOTALS"],
    ["Recurring System (Hub + 3 Agencies)", num(nhFiveYearGrandTotal)],
    ["Developer Group (2-Yr Firm + 3-Yr Options)", num(nhDevGroup.totals.fiveYearAllOptions)],
    ["GRAND TOTAL · 5-YEAR ALL-IN", num(nhFiveYearAllInTotal)],
    [],
    ["5-YEAR OUTCOME TOTALS"],
    ["Cumulative Household-Years of Service", nhProgramGoalsTotals.householdYears],
    ["LSNH / HCC North Enrollments (Cumulative)", nhProgramGoalsTotals.lonestarEnrollmentTotal],
    ["LSNH / HCC North Graduates (Cumulative)", nhProgramGoalsTotals.lonestarGraduatesTotal],
    ["IAH-Corridor Job Placements (Cumulative)", nhProgramGoalsTotals.corridorPlacementsTotal],
    ["Section 8 Recertifications · On-Time (Cumulative)", nhProgramGoalsTotals.section8RecertsTotal],
    ["Section 8 → Homeownership Transitions (Cumulative)", nhProgramGoalsTotals.homeownershipTransitionsTotal],
    ["Senior Home Modifications (Cumulative)", nhProgramGoalsTotals.homeModsTotal],
    ["Seniors Aging-in-Place Stabilized (Cumulative)", nhProgramGoalsTotals.agingInPlaceStabilizedTotal],
    ["Medicare / Extra Help Enrollments (Cumulative)", nhProgramGoalsTotals.medicareEnrollmentsTotal],
    ["Public Benefits Recovered for Households", nhProgramGoalsTotals.benefitsRecoveredTotal],
    ["Evictions Prevented (Cumulative)", nhProgramGoalsTotals.evictionsPreventedTotal],
    [],
    ["SCALE NOTE"],
    ["North Houston District operates at ~80% of the Baytown baseline (1,200 HH vs 1,500 HH)."],
    [],
    ["NOTES"],
    ["3% annual cost-of-living raise on all salaried positions."],
    ["Non-salary operating costs (software, evaluation, admin, office ops) held flat."],
    ["Developer Group declines year-over-year as North Houston District achieves operational independence."],
    ["Years 3–5 of Developer Group are District Options (not firm commitments)."],
    ["This is a PROPOSED budget. Figures are illustrative and subject to negotiation with District leadership."],
  ];
  return buildSheet(rows, { colWidths: [52, 20, 20] });
}

// ---------- 2. 5-Year Forecast ----------
function fiveYearForecastSheet() {
  const header = ["Year", "Central Hub", "Per Agency", "Total 3 Agencies", "Recurring Subtotal", "Developer Group", "Total All-In", "$ / HH (Recurring)", "$ / HH (All-In)"];
  const rows = [
    ["NORTH HOUSTON DISTRICT · 5-YEAR FINANCIAL FORECAST"],
    [`Households Served per Year: 1,200 · 3 Spokes × 400 HH · Caseload Cap: 1:${nhSystemTotals.caseload} · 3% annual COLA`],
    [],
    header,
    ...nhFiveYearForecast.map((y) => [y.year, y.hub, y.perAgency, y.totalAgencies, y.system, y.devGroup, y.systemAllIn, y.costPerHH, y.costPerHHAllIn]),
    [],
    ["5-YEAR TOTAL",
      nhFiveYearForecast.reduce((s, y) => s + y.hub, 0),
      "—",
      nhFiveYearForecast.reduce((s, y) => s + y.totalAgencies, 0),
      nhFiveYearGrandTotal,
      nhDevGroup.totals.fiveYearAllOptions,
      nhFiveYearAllInTotal,
      "—",
      "—",
    ],
  ];
  return buildSheet(rows, { colWidths: [14, 14, 14, 18, 18, 16, 16, 18, 18] });
}

// ---------- 3. Year 1 · Central Hub Budget ----------
function hubBudgetSheet() {
  const rows = [
    ["PART A · CENTRAL COORDINATION HUB (NORTH HOUSTON DISTRICT)"],
    ["Year 1 Detailed Line Items · Permanent NH District infrastructure"],
    [],
    ["Line Item", "Detail", "Year 1 Cost"],
    ...nhCentralHubLines.map((l) => [l.line, l.detail, l.cost]),
    [],
    ["HUB TOTAL · YEAR 1", "", nhSystemTotals.centralHub],
  ];
  return buildSheet(rows, { colWidths: [46, 90, 16] });
}

// ---------- 4. Year 1 · Per-Agency Budget ----------
function agencyBudgetSheet() {
  const rows = [
    ["PART B · PER-AGENCY OPERATING BUDGET (× 3 SPOKES)"],
    [`Each agency manages 400 households / year · Caseload 1:${nhSystemTotals.caseload}`],
    [],
    ["Role", "FTE", "Detail", "Year 1 Cost"],
    ...nhAgencyBudgetLines.map((l) => [l.role, l.fte, l.detail, l.cost]),
    [],
    ["PER-AGENCY TOTAL", "", "", nhSystemTotals.perAgency],
    [`× ${nhSystemTotals.agencyCount} AGENCIES`, "", "", nhSystemTotals.totalAgencies],
  ];
  return buildSheet(rows, { colWidths: [40, 8, 78, 16] });
}

// ---------- 5. Developer Group · Sterile Mate LLC ----------
function developerGroupSheet() {
  const rows = [
    ["PART C · STERILE MATE DEVELOPER GROUP"],
    ["2-year firm engagement + 3-year District options · Builds & hands off to North Houston District"],
    [],
    ["TEAM ROSTER · YEAR 1"],
    ["Role", "Who", "Detail", "Year 1 Cost"],
    ...nhDevGroup.team.map((m) => [m.role, m.who, m.detail, m.cost]),
    ["", "", "Team Salaries Subtotal (Yr 1)", nhDevGroup.totals.teamSalariesYr1],
    ["", "", "Direct Expenses (curriculum, materials, travel)", nhDevGroup.totals.directExpensesYr1],
    ["", "", "YEAR 1 DEVELOPER GROUP TOTAL", nhDevGroup.totals.year1],
    [],
    ["MULTI-YEAR CONTRACT SCHEDULE"],
    ["Period", "Phase", "Detail", "Cost"],
    ...nhDevGroup.contract.map((c) => [c.period, c.phase, c.detail, c.cost]),
    [],
    ["2-YEAR FIRM COMMITMENT", "", "", nhDevGroup.totals.twoYearFirm],
    ["5-YEAR (if all options exercised)", "", "", nhDevGroup.totals.fiveYearAllOptions],
  ];
  return buildSheet(rows, { colWidths: [34, 38, 78, 16] });
}

// ---------- 6. Tri-Portfolio Contracts ----------
function portfolioSheet() {
  const rows = [
    ["TRI-PORTFOLIO CONTRACT STRUCTURE"],
    ["Three competitively-selected nonprofit Spokes · 5-yr contract, annual review"],
    [],
    ["#", "Portfolio", "Households", "Annual Contract", "Focus"],
    ...nhPortfolioContracts.map((p) => [p.id, p.name, p.households, p.cost, p.focus]),
    [],
    ["TOTAL", "3 Spokes", nhSystemTotals.householdsServed, nhSystemTotals.totalAgencies, ""],
  ];
  return buildSheet(rows, { colWidths: [4, 42, 12, 16, 78] });
}

// ---------- 7. Funding Sources Stack ----------
function fundingSourcesSheet() {
  const rows = [
    ["FUNDING STACK · WHERE THE YEAR-1 DOLLARS COME FROM"],
    [`Illustrative funding-mix scenario for the ${"$" + nhSystemTotals.grandTotal.toLocaleString()} Year-1 ask`],
    [],
    ["Source", "Share (%)", "Estimated Year-1 $", "Notes"],
    ...nhFundingSources.map((f) => [f.source, f.pct, f.amount, f.note]),
    [],
    ["TOTAL", 100, nhSystemTotals.grandTotal, ""],
    [],
    ["AGGREGATE SHARES"],
    ["Federal Share (CDBG + WIOA + ESG/CoC)", "~63%", 2176003, ""],
    ["Regional & County (ARPA + TIRZ)", "~17%", 587445, ""],
    ["Private Match (Anchor + Philanthropic)", "~20%", 691112, ""],
    [],
    ["Final mix locked in during the District Board resolution and grant-writing phase (Months 1–3)."],
  ];
  return buildSheet(rows, { colWidths: [52, 14, 20, 80] });
}

// ---------- 8. Regional Hub Consolidation ----------
function regionalHubSheet() {
  const rows = [
    ["REGIONAL HUB CONSOLIDATION · SCALING SCENARIOS"],
    ["How the Hub line item behaves as 2/3/4 Harris County proposals go live"],
    [],
    ["Scenario", "Deployments", "Households Total", "Hub Total Cost", "Hub / Deployment", "Hub / HH / Year", "Note"],
    ...nhRegionalScenarios.map((s) => [s.label, s.deployments, s.households, s.hubTotal, s.hubPerDeployment, s.hubPerHH, s.note]),
    [],
    ["KEY INSIGHT"],
    ["If Harris County funds the Hub centrally as a County General-Fund or CDBG County-Allocation line item, the North Houston District's direct ask drops to $2,749,500 — just the three Spokes."],
    [],
    ["RECOMMENDED PATH"],
    ["Launch NH Proposal 02 with the full single-deployment Hub embedded ($3,455,560 Year-1 ask). At the Year-2 contract renewal — once Baytown Proposal 01 is in flight — formally migrate the Hub to a Harris County Regional Coordination Unit and re-baseline both deployments' per-Spoke contracts. Council and the District Board approve the consolidation in concert."],
  ];
  return buildSheet(rows, { colWidths: [22, 14, 18, 18, 20, 18, 70] });
}

// ---------- 9. Operational Phases / Launch Plan ----------
function launchPlanSheet() {
  const rows = [
    ["YEAR-1 OPERATIONAL LAUNCH PLAN"],
    ["From Board resolution to 1,200 active households in six months"],
    [],
    ["Period", "Milestone", "Description"],
    ...nhOperationalPhases.map((p) => [p.period, p.title, p.body]),
  ];
  return buildSheet(rows, { colWidths: [16, 54, 96] });
}

// ---------- 10. ROI / Cost Avoidance ----------
function roiSheet() {
  const rows = [
    ["ECONOMIC ROI · COMPARED TO WHAT?"],
    [],
    ["Item", "Cost", "Note"],
    ...nhTriPortfolioROI.map((r) => [r.label, r.value, r.note]),
  ];
  return buildSheet(rows, { colWidths: [42, 22, 88] });
}

// ---------- 11. Referral Partners ----------
function partnersSheet() {
  const rows = [
    ["REFERRAL PARTNER NETWORK"],
    [],
    ["Partner Organization", "Role", "Track"],
    ...nhReferralPartners.map((p) => [p.name, p.role, p.track]),
  ];
  return buildSheet(rows, { colWidths: [38, 78, 14] });
}

// ---------- 12. Program Goals Forecast (5-year outcome targets) ----------
function programGoalsSheet() {
  const header = [
    "Year",
    "Households",
    "Seniors",
    "Families",
    "LSNH / HCC North · Enrolled",
    "LSNH / HCC North · Graduates",
    "IAH-Corridor Placements",
    "Section 8 Recertifications (on-time)",
    "Senior Home Modifications",
    "Medicare / Extra Help Enrollments",
    "Benefits Recovered ($)",
    "Evictions Prevented",
    "Seniors Aging-in-Place Stabilized",
    "Section 8 → Homeownership",
    "Note",
  ];
  const rows = [
    ["PROGRAM GOALS FORECAST · 5-YEAR OUTCOME TARGETS"],
    ["Scaled to 1,200 households · 3 Spokes × 400 HH · IAH-Corridor employer pipeline"],
    [],
    header,
    ...nhProgramGoalsForecast.map((y) => [
      y.year, y.households, y.seniors, y.families,
      y.lonestarEnrollment, y.lonestarGraduates, y.corridorPlacements,
      y.section8Recerts, y.homeMods, y.medicareEnrollment,
      y.benefitsRecovered, y.evictionsPrevented, y.agingInPlaceStabilized,
      y.homeownershipTransitions, y.note,
    ]),
    [],
    [
      "5-YEAR TOTAL",
      `${nhProgramGoalsTotals.householdYears.toLocaleString()} household-yrs`,
      "—", "—",
      nhProgramGoalsTotals.lonestarEnrollmentTotal,
      nhProgramGoalsTotals.lonestarGraduatesTotal,
      nhProgramGoalsTotals.corridorPlacementsTotal,
      nhProgramGoalsTotals.section8RecertsTotal,
      nhProgramGoalsTotals.homeModsTotal,
      nhProgramGoalsTotals.medicareEnrollmentsTotal,
      nhProgramGoalsTotals.benefitsRecoveredTotal,
      nhProgramGoalsTotals.evictionsPreventedTotal,
      nhProgramGoalsTotals.agingInPlaceStabilizedTotal,
      nhProgramGoalsTotals.homeownershipTransitionsTotal,
      `Cumulative outcomes across 1,200-HH cohort over 5 service years`,
    ],
  ];
  return buildSheet(rows, { colWidths: [10, 12, 10, 10, 22, 22, 22, 24, 22, 24, 18, 18, 26, 22, 96] });
}

// ---------- 13. Program Outcome Benchmarks ----------
function outcomeBenchmarksSheet() {
  const rows = [
    ["PROGRAM OUTCOME BENCHMARKS"],
    ["Year-over-year program-wide fidelity benchmarks · Independent evaluator verifies annually"],
    [],
    ["Metric", "Target", "Rationale"],
    ...nhProgramOutcomeBenchmarks.map((b) => [b.metric, b.target, b.rationale]),
  ];
  return buildSheet(rows, { colWidths: [54, 26, 110] });
}

// ---------- Master export ----------
export function generateNHBudgetSpreadsheet() {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, execSummarySheet(), "Executive Summary");
  XLSX.utils.book_append_sheet(wb, fiveYearForecastSheet(), "5-Year Forecast");
  XLSX.utils.book_append_sheet(wb, programGoalsSheet(), "Program Goals Forecast");
  XLSX.utils.book_append_sheet(wb, outcomeBenchmarksSheet(), "Outcome Benchmarks");
  XLSX.utils.book_append_sheet(wb, hubBudgetSheet(), "Yr1 · Hub Budget");
  XLSX.utils.book_append_sheet(wb, agencyBudgetSheet(), "Yr1 · Per-Agency Budget");
  XLSX.utils.book_append_sheet(wb, developerGroupSheet(), "Developer Group");
  XLSX.utils.book_append_sheet(wb, portfolioSheet(), "Tri-Portfolio Contracts");
  XLSX.utils.book_append_sheet(wb, fundingSourcesSheet(), "Funding Sources");
  XLSX.utils.book_append_sheet(wb, regionalHubSheet(), "Regional Hub Scenarios");
  XLSX.utils.book_append_sheet(wb, launchPlanSheet(), "Launch Plan");
  XLSX.utils.book_append_sheet(wb, roiSheet(), "ROI · Cost Avoidance");
  XLSX.utils.book_append_sheet(wb, partnersSheet(), "Referral Partners");

  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `NorthHoustonDistrict-Proposal-02-5Yr-Financials-${stamp}.xlsx`);
}
